import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import Button from "../foundation/Button";
import { submitMerchantWaitlist } from "@/lib/api";

/**
 * Reusable email capture form for the ZenMe marketing site.
 *
 * Phase 1: visual only — no backend wiring. Success state is local.
 * Backend integration ships in Phase 4.
 *
 * Props:
 *   variant: "horizontal" | "stacked" (default "horizontal" on desktop, stacked on mobile)
 *   size: "md" | "lg" (default "md")
 *   ctaLabel: string (default "Join Waitlist")
 *   placeholder: string (default "you@company.com")
 *   onSubmit?: (email) => Promise<void> | void — optional hook for future wiring
 *   className: string
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MerchantWaitlist = ({
  size = "md",
  ctaLabel = "Join Waitlist",
  placeholder = "you@company.com",
  onSubmit,
  source = "generic",
  successMessage = "Thanks — we'll be in touch.",
  className,
  "data-testid": testId = "merchant-waitlist",
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  // Derive a unique input id per instance so multiple forms on the same page
  // (e.g. hero inline + modal) don't share duplicate DOM ids or a broken
  // <label htmlFor=…> association.
  const inputId = React.useMemo(
    () =>
      `waitlist-email-${source}-${Math.random().toString(36).slice(2, 8)}`,
    [source],
  );
  const errorId = `${inputId}-error`;

  const inputHeight = size === "lg" ? "h-14" : "h-12";
  const inputText = size === "lg" ? "text-base" : "text-[15px]";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;

    const trimmed = email.trim();
    if (!trimmed) {
      setError("Please enter your email address.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setStatus("submitting");

    try {
      const payload = { email: trimmed, source };
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await submitMerchantWaitlist(payload);
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err?.message || "Something went wrong. Please try again in a moment.");
    }
  };

  return (
    <div className={cn("w-full max-w-xl", className)} data-testid={testId}>
      <AnimatePresence mode="wait" initial={false}>
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className="flex items-center gap-3 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-4 py-3"
            data-testid="form-success"
            role="status"
            aria-live="polite"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-emerald text-black">
              <Check className="h-4 w-4" strokeWidth={3} />
            </span>
            <p className="text-sm text-white">
              {successMessage}
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            noValidate
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "flex w-full flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-2",
            )}
            data-testid="waitlist-form"
          >
            <label htmlFor={inputId} className="sr-only">
              Email address
            </label>
            <div className="relative flex-1">
              <input
                id={inputId}
                type="email"
                inputMode="email"
                autoComplete="email"
                spellCheck={false}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                placeholder={placeholder}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                className={cn(
                  "w-full rounded-full border bg-white/[0.03] px-5 text-white placeholder:text-text-muted",
                  "transition-colors duration-200 outline-none",
                  "focus:border-brand-emerald/60 focus:bg-white/[0.05]",
                  error
                    ? "border-red-500/60"
                    : "border-white/[0.10] hover:border-white/20",
                  inputHeight,
                  inputText,
                )}
                data-testid="waitlist-email-input"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size={size === "lg" ? "lg" : "md"}
              disabled={status === "submitting"}
              rightIcon={
                status === "submitting" ? null : (
                  <ArrowRight className="h-4 w-4" />
                )
              }
              className="sm:min-w-[160px]"
              data-testid="waitlist-submit-btn"
            >
              {status === "submitting" ? "Submitting…" : ctaLabel}
            </Button>
          </motion.form>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.p
            id={errorId}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            className="mt-2 pl-1 text-xs text-red-400"
            data-testid="waitlist-error"
            role="alert"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      {status !== "success" && (
        <p className="mt-3 pl-1 text-[11px] text-text-muted">
          No spam. Unsubscribe anytime.
        </p>
      )}
    </div>
  );
};

export default MerchantWaitlist;
